// Чем запрос панели доказывает, кто он (26 сен 2026, спека
// docs/superpowers/specs/2026-09-26-admin-passkey-access-verification.md, пункты 3 и 5).
//
// Два способа, и только здесь они превращаются в заголовок и адрес живого канала:
//
//   • ПРОПУСК — строка (JWT). Мини-апп (вход из Telegram) и старый веб-вход по паролю. Едет
//     заголовком `Authorization: Bearer …`, а у живых каналов (EventSource и WebSocket заголовков
//     не умеют) — параметром `?token=`.
//   • ЖИВАЯ СЕССИЯ АДМИНА — `ADMIN_SESSION`. Вход ключом доступа на panel.padla.pro: кука
//     `__Host-padla_admin` с флагом HttpOnly. Странице она не видна и не нужна — браузер сам несёт
//     её в `fetch`, `EventSource` и `WebSocket` того же адреса. Поэтому у сессии нет ни заголовка,
//     ни `?token=`, ни срока, который страница могла бы проверить: жива ли она, решает шлюз на
//     каждом запросе (роль — из базы).
//
// ЗАЧЕМ ТИП, А НЕ ПУСТАЯ СТРОКА. До 26 сен двенадцать мест админки сами вынимали пропуск из
// заголовка, а хуки запросов включались по `!!token`. Сессия по куке строки не имеет: пустая
// строка в любом из этих мест молча выключила бы ленту, журналы и ЦУП. `ADMIN_SESSION` — истинное
// значение («вход есть»), которое никогда не становится заголовком.

import { createContext, useContext } from 'react';

export interface AdminSession {
  readonly kind: 'admin-session';
}

/** Живая сессия админа по куке. Одна на приложение — сравнивается по ссылке. */
export const ADMIN_SESSION: AdminSession = Object.freeze({ kind: 'admin-session' as const });

/** Пропуск (строка JWT) или живая сессия админа. */
export type PanelAuth = string | AdminSession;

export function isAdminSession(auth: PanelAuth | null | undefined): auth is AdminSession {
  return typeof auth === 'object' && auth !== null && auth.kind === 'admin-session';
}

/** Строка пропуска, если это пропуск; у сессии — null (кука странице не видна). */
export function bearerTokenOf(auth: PanelAuth | null | undefined): string | null {
  return typeof auth === 'string' && auth !== '' ? auth : null;
}

/** Заголовки запроса: пропуск → `Authorization`, сессия → ничего (куку несёт браузер). */
export function authHeaders(auth: PanelAuth | null | undefined): Record<string, string> {
  const token = bearerTokenOf(auth);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Адрес живого канала (EventSource / WebSocket): пропуск дописывается `token=`, у сессии адрес
 * остаётся как есть — канал того же адреса несёт куку сам.
 */
export function withChannelAuth(url: string, auth: PanelAuth | null | undefined): string {
  const token = bearerTokenOf(auth);
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}

/**
 * Чем входит текущая админка — ставит `AdminPanel`, читают её экраны и живые каналы. Экраны не
 * выводят способ входа из `headers` («нет заголовка — значит сессия» — догадка, которая молча
 * стала бы правдой и для экрана без входа вовсе): вне админки контекст пуст, и каналы не открываются.
 */
export const PanelAuthContext = createContext<PanelAuth | null>(null);

export function usePanelAuth(): PanelAuth | null {
  return useContext(PanelAuthContext);
}
